import React, { useState, useContext, useCallback } from 'react';

type VoidFn = () => void;

export type DrawerContextStateType = {
  /** Function to perform when changing internal drawer state, but not closing the drawer entirely; i.e. navigating back to previous screen */
  onBack?: VoidFn;
  /** Function to perform when attempting to close the drawer entirely; */
  onClose?: VoidFn;
  /** Function to perform when attempting to save the actionable interal children of the drawer; Does NOT close the drawer upon completion, but onClose can be included in onSave */
  onSave?: VoidFn;
  /** Function to catch unsaved changes, fires when trying to exit the drawer (or can be called in react-router when trying to leave route) */
  onUnsavedChanges?: VoidFn;
  /** Drawer is visible state */
  isOpen?: boolean;
  /** Show the unsaved changes prompt */
  showSavePrompt?: boolean;
};

const defaultState: DrawerContextStateType = {
  onBack: undefined,
  onClose: undefined,
  onSave: undefined,
  onUnsavedChanges: undefined,
  isOpen: false,
  showSavePrompt: false,
};

export const DrawerContext = React.createContext<
  DrawerContextStateType & {
    /* Callback setter for onBack */
    setOnBack: (onBack?: VoidFn) => void;
    /* Callback setter for onClose */
    setOnClose: (onClose?: VoidFn) => void;
    /* Callback setter for onSave */
    setOnSave: (onSave?: VoidFn) => void;
    /* Callback setter for onUnsavedChanges */
    setOnUnsavedChanges: (onUnsavedChanges?: VoidFn) => void;
    /* State setter for isOpen */
    setIsOpen: (isOpen: boolean) => void;
    /* State setter for showSavePrompt */
    setShowSavePrompt: (showSavePrompt: boolean) => void;
  }
>({
  ...defaultState,
  setOnBack: () => undefined,
  setOnClose: () => undefined,
  setOnSave: () => undefined,
  setOnUnsavedChanges: () => undefined,
  setIsOpen: () => undefined,
  setShowSavePrompt: () => undefined,
});

const { Provider } = DrawerContext;

export const DrawerProvider: React.FC<DrawerContextStateType> = ({ children, ...props }) => {
  const [drawerState, setDrawerState] = useState({ ...defaultState, ...props });
  const [, setUnsavedCbs] = useState<VoidFn[]>([]);

  const setOnBack = useCallback((onBack?: VoidFn) => {
    setDrawerState(prevState => ({ ...prevState, onBack }));
  }, []);

  const setOnClose = useCallback((onClose?: VoidFn) => {
    setDrawerState(prevState => ({ ...prevState, onClose }));
  }, []);

  const setOnSave = useCallback((onSave?: VoidFn) => {
    setDrawerState(prevState => ({ ...prevState, onSave }));
  }, []);

  const setOnUnsavedChanges = useCallback((onUnsavedChanges?: VoidFn) => {
    if (onUnsavedChanges) {
      setUnsavedCbs(prevUnsavedCbs => {
        const newUnsavedCbs = [onUnsavedChanges, ...prevUnsavedCbs];
        setDrawerState(prevState => ({ ...prevState, onUnsavedChanges: newUnsavedCbs[0] }));
        return newUnsavedCbs;
      });
    } else {
      setUnsavedCbs(prevUnsavedCbs => {
        const [, ...rest] = prevUnsavedCbs;
        setDrawerState(prevState => ({ ...prevState, onUnsavedChanges: rest[0] }));
        return rest;
      });
    }
  }, []);

  const setIsOpen = useCallback((isOpen: boolean) => {
    setDrawerState(prevState => ({ ...prevState, isOpen }));
  }, []);

  const setShowSavePrompt = useCallback((showSavePrompt: boolean) => {
    setDrawerState(prevState => ({ ...prevState, showSavePrompt }));
  }, []);

  return (
    <Provider
      value={{
        ...drawerState,
        setOnBack,
        setOnClose,
        setOnSave,
        setOnUnsavedChanges,
        setIsOpen,
        setShowSavePrompt,
      }}
    >
      {children}
    </Provider>
  );
};

export const useDrawerControls = () => useContext(DrawerContext);
